import { Config } from "atoms/Ltool/newConfigAtoms";
import type { AchievementLevelData } from "atoms/Ltool/matrixAtoms";
import type { Powertrain } from "atoms/powertrainAtom";
import { toAchievementLevelData } from "utils/achievementLevelResponse";
import { toGraphData } from "utils/graphDataResponse";
import { throwIfNotOk } from "./apiError";

// URL の powertrain セグメント（/assess/<powertrain>/...）は引数で切り替える（既定 "bev"）。
// L 側 HEV は calculation→unit→gear→condition の階層で、unit 配下に gear が入る。
// gear を含むエンドポイント（dimensions/achievement/copy/描画）は gear 対応版を別途用意し、
// 本ファイルでは gear 不要の階層一覧・計算セット CRUD のみ prefix を引数化する（HEV対応仕様 §3.2）。

// 計算セット一覧取得
export const fetchProjects = async (
    authToken: string,
    powertrain: Powertrain = "bev",
) => {
    const baseURL = process.env.REACT_APP_URL;
    const url = `${baseURL}/assess/${powertrain}/projects`.replace(/\s+/g, "");

    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
        }
    });

    await throwIfNotOk(response, "fetchLtool");

    const body = await response.json();
    return body.data.project_id;
};

// 仕向け地一覧取得
export const fetchDestinations = async (
    authToken: string,
    config: Config,
    powertrain: Powertrain = "bev",
) => {
    const baseURL = process.env.REACT_APP_URL;
    const url = `${baseURL}/assess/${powertrain}/projects
    /${encodeURIComponent(config.project_id)}
    /destinations`.replace(/\s+/g, "");

    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
        }
    });

    await throwIfNotOk(response, "fetchLtool");

    const body = await response.json();
    return body.data.destination_id;
};

// ロット一覧取得
export const fetchLots = async (
    authToken: string,
    config: Config,
    powertrain: Powertrain = "bev",
) => {
    const baseURL = process.env.REACT_APP_URL;
    const url = `${baseURL}/assess/${powertrain}/projects
    /${encodeURIComponent(config.project_id)}
    /${encodeURIComponent(config.destination_id)}
    /lots`.replace(/\s+/g, "");

    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
        }
    });

    await throwIfNotOk(response, "fetchLtool");

    const body = await response.json();
    return body.data.lot_id;
};

// フェーズ一覧取得
export const fetchPhases = async (
    authToken: string,
    config: Config,
    powertrain: Powertrain = "bev",
) => {
    const baseURL = process.env.REACT_APP_URL;
    const url = `${baseURL}/assess/${powertrain}/projects
    /${encodeURIComponent(config.project_id)}
    /${encodeURIComponent(config.destination_id)}
    /${encodeURIComponent(config.lot_id)}
    /phases`.replace(/\s+/g, "");

    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
        }
    });

    await throwIfNotOk(response, "fetchLtool");

    const body = await response.json();
    return body.data.phase_id;
};

// 計算ID一覧取得
export const fetchCalculations = async (
    authToken: string,
    config: Config,
    powertrain: Powertrain = "bev",
) => {
    const baseURL = process.env.REACT_APP_URL;
    const url = `${baseURL}/assess/${powertrain}/projects
    /${encodeURIComponent(config.project_id)}
    /${encodeURIComponent(config.destination_id)}
    /${encodeURIComponent(config.lot_id)}
    /${encodeURIComponent(config.phase_id)}
    /calculations`.replace(/\s+/g, "");

    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
        }
    });

    await throwIfNotOk(response, "fetchLtool");

    const body = await response.json();
    return body.data.calculation_id;
};

// 諸元: gear_ratio / motor_order とも {phenomenon_id: float} のフラット形式（BEV/HEV共通）。
// 未登録時も 200 + 空 dict（gear_ratio/motor_order は {}、tire_radius は null）で返る
// （諸元データ仕様変更 #27。envelope は無く、レスポンス body がそのまま dict）。
export type DimensionsData = {
    tire_radius: number | null;
    gear_ratio: Record<string, number>;
    motor_order: Record<string, number>;
};

export type DimensionConfig = Pick<
    Config,
    "project_id" | "destination_id" | "lot_id" | "phase_id" | "calculation_id"
>;

export type HevDimensionsData = DimensionsData;

// 諸元データ取得
export const fetchDimensions = async (
    authToken: string,
    config: DimensionConfig,
    unit_id: string,
): Promise<DimensionsData> => {
    const baseURL = process.env.REACT_APP_URL;

    const url = `${baseURL}/assess/bev/projects
    /${encodeURIComponent(config.project_id)}
    /${encodeURIComponent(config.destination_id)}
    /${encodeURIComponent(config.lot_id)}
    /${encodeURIComponent(config.phase_id)}
    /${encodeURIComponent(config.calculation_id)}
    /datasets/units
    /${encodeURIComponent(unit_id)}
    /dimensions/all`.replace(/\s+/g, "");

    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
        }
    });

    await throwIfNotOk(response, "fetchLtool");

    return await response.json();
};

// GET achievement_config は常に 200（未登録でもデフォルト値で補完される）。
// 登録済み値かデフォルト値かを示すフラグは無いため、FE は区別せずそのまま表示する。
export type AchievementConfigData = {
    three_sigma: Record<string, number>;
    risk_margin: Record<string, number>;
    freq: Record<string, { max: number | null; min: number | null }>;
};

export type AchievementConfigConfig = Pick<
    Config,
    "project_id" | "destination_id" | "lot_id" | "phase_id" | "calculation_id"
>;

export type AchievementConfigResponse = {
    message: string;
    data: AchievementConfigData;
};

// 作成済み計算セットのメタ情報取得
export type CalculationSetData = {
    description: string;
    units: string[];
};

export type CalculationSetConfig = Pick<
    Config,
    "project_id" | "destination_id" | "lot_id" | "phase_id" | "calculation_id"
>;

export const fetchCalculationSet = async (
    authToken: string,
    config: CalculationSetConfig,
    powertrain: Powertrain = "bev",
): Promise<CalculationSetData> => {
    const baseURL = process.env.REACT_APP_URL;

    const url = `${baseURL}/assess/${powertrain}/projects
    /${encodeURIComponent(config.project_id)}
    /${encodeURIComponent(config.destination_id)}
    /${encodeURIComponent(config.lot_id)}
    /${encodeURIComponent(config.phase_id)}
    /${encodeURIComponent(config.calculation_id)}`.replace(/\s+/g, "");

    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
        }
    });

    await throwIfNotOk(response, "fetchLtool");

    const body = await response.json();
    return body.data;
};

// 達成度設定取得
export const fetchAchievementConfig = async (
    authToken: string,
    config: AchievementConfigConfig,
): Promise<AchievementConfigData> => {
    const baseURL = process.env.REACT_APP_URL;

    const url = `${baseURL}/assess/bev/projects
    /${encodeURIComponent(config.project_id)}
    /${encodeURIComponent(config.destination_id)}
    /${encodeURIComponent(config.lot_id)}
    /${encodeURIComponent(config.phase_id)}
    /${encodeURIComponent(config.calculation_id)}
    /achievement_config`.replace(/\s+/g, "");

    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
        }
    });

    await throwIfNotOk(response, "fetchLtool");

    const body: AchievementConfigResponse = await response.json();
    return body.data;
};

// 達成度判断用のデータの取得（レスポンス直下に risk_margin を含む）
export const fetchMatrixData = async (
    authToken: string,
    config: Config,
): Promise<AchievementLevelData> => {
    const baseURL = process.env.REACT_APP_URL;

    const url = `${baseURL}/assess/bev/projects
    /${encodeURIComponent(config.project_id)}
    /${encodeURIComponent(config.destination_id)}
    /${encodeURIComponent(config.lot_id)}
    /${encodeURIComponent(config.phase_id)}
    /${encodeURIComponent(config.calculation_id)}
    /achievement_level`.replace(/\s+/g, "");

    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
        }
    });

    await throwIfNotOk(response, "fetchLtool");

    const body: unknown = await response.json();
    return toAchievementLevelData(body);
};

// グラフ描画用のデータの取得
export const fetchGraphData = async (
    authToken: string,
    config: Config,
    unit_id: string,
) => {
    const baseURL = process.env.REACT_APP_URL;

    const url = `${baseURL}/assess/bev/projects
    /${encodeURIComponent(config.project_id)}
    /${encodeURIComponent(config.destination_id)}
    /${encodeURIComponent(config.lot_id)}
    /${encodeURIComponent(config.phase_id)}
    /${encodeURIComponent(config.calculation_id)}
    /achievement_level
    /${encodeURIComponent(unit_id)}
    /${encodeURIComponent(config.condition_id)}`.replace(/\s+/g, "");

    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
        }
    });

    await throwIfNotOk(response, "fetchLtool");

    const body: unknown = await response.json();
    return toGraphData(body);
};

// ============================================================
// HEV 専用（L 階層は calc→unit→gear→condition。openapi assess.yaml の HEV path に対応）
// achievement_level/config・frequency-conversion は gear 単位 URL（Cycle2仕様変更_part1）。
// dimensions は .../units/<unit>/<gear>/dimensions/all。body/レスポンスはフラット形式を維持。
// ============================================================

// HEV: gear 一覧取得（当該 (calculation, unit) に登録済みの gear_id を返す。L は URL に unit を含む）
export const fetchGears = async (
    authToken: string,
    config: CalculationSetConfig,
    unit_id: string,
): Promise<string[]> => {
    const baseURL = process.env.REACT_APP_URL;
    const url = `${baseURL}/assess/hev/projects
    /${encodeURIComponent(config.project_id)}
    /${encodeURIComponent(config.destination_id)}
    /${encodeURIComponent(config.lot_id)}
    /${encodeURIComponent(config.phase_id)}
    /${encodeURIComponent(config.calculation_id)}
    /units
    /${encodeURIComponent(unit_id)}
    /gears`.replace(/\s+/g, "");

    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
        }
    });

    await throwIfNotOk(response, "fetchLtool");

    const body = await response.json();
    return body.data.gear_id;
};

// HEV: 諸元データ取得（unit 配下に gear）
export const fetchDimensionsHev = async (
    authToken: string,
    config: DimensionConfig,
    unit_id: string,
    gear_id: string,
): Promise<HevDimensionsData> => {
    const baseURL = process.env.REACT_APP_URL;

    const url = `${baseURL}/assess/hev/projects
    /${encodeURIComponent(config.project_id)}
    /${encodeURIComponent(config.destination_id)}
    /${encodeURIComponent(config.lot_id)}
    /${encodeURIComponent(config.phase_id)}
    /${encodeURIComponent(config.calculation_id)}
    /datasets/units
    /${encodeURIComponent(unit_id)}
    /${encodeURIComponent(gear_id)}
    /dimensions/all`.replace(/\s+/g, "");

    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
        }
    });

    await throwIfNotOk(response, "fetchLtool");

    return await response.json();
};

// HEV: 達成度設定取得（gear 単位 URL。レスポンスの freq は指定 gear 分のフラット形式）
export const fetchAchievementConfigHev = async (
    authToken: string,
    config: AchievementConfigConfig,
    gear_id: string,
): Promise<AchievementConfigData> => {
    const baseURL = process.env.REACT_APP_URL;

    const url = `${baseURL}/assess/hev/projects
    /${encodeURIComponent(config.project_id)}
    /${encodeURIComponent(config.destination_id)}
    /${encodeURIComponent(config.lot_id)}
    /${encodeURIComponent(config.phase_id)}
    /${encodeURIComponent(config.calculation_id)}
    /${encodeURIComponent(gear_id)}
    /achievement_config`.replace(/\s+/g, "");

    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
        }
    });

    await throwIfNotOk(response, "fetchLtool");

    const body: AchievementConfigResponse = await response.json();
    return body.data;
};

// HEV: 達成度判断表の描画用データ取得（gear 単位 URL: GET /assess/hev/.../{calc}/{gear}/achievement_level）
// レスポンスは BEV と同じ `unit > condition` の 2 階層で、URL の gear 分だけが返る（#50 / api#134）。
// 旧 URL（gear 無し）は BE で廃止済み（404）で、BE と同時デプロイが前提。
export const fetchMatrixDataHev = async (
    authToken: string,
    config: Config,
    gear_id: string,
): Promise<AchievementLevelData> => {
    const baseURL = process.env.REACT_APP_URL;

    const url = `${baseURL}/assess/hev/projects
    /${encodeURIComponent(config.project_id)}
    /${encodeURIComponent(config.destination_id)}
    /${encodeURIComponent(config.lot_id)}
    /${encodeURIComponent(config.phase_id)}
    /${encodeURIComponent(config.calculation_id)}
    /${encodeURIComponent(gear_id)}
    /achievement_level`.replace(/\s+/g, "");

    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
        }
    });

    await throwIfNotOk(response, "fetchLtool");

    const body: unknown = await response.json();
    return toAchievementLevelData(body);
};

// HEV: グラフ描画用データ取得（unit と condition の間に gear が入る）
export const fetchGraphDataHev = async (
    authToken: string,
    config: Config,
    unit_id: string,
    gear_id: string,
) => {
    const baseURL = process.env.REACT_APP_URL;

    const url = `${baseURL}/assess/hev/projects
    /${encodeURIComponent(config.project_id)}
    /${encodeURIComponent(config.destination_id)}
    /${encodeURIComponent(config.lot_id)}
    /${encodeURIComponent(config.phase_id)}
    /${encodeURIComponent(config.calculation_id)}
    /achievement_level
    /${encodeURIComponent(unit_id)}
    /${encodeURIComponent(gear_id)}
    /${encodeURIComponent(config.condition_id)}`.replace(/\s+/g, "");

    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken,
        }
    });

    await throwIfNotOk(response, "fetchLtool");

    const body: unknown = await response.json();
    return toGraphData(body);
};
